/**
 * IMPORTS
 */

import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useStoreZustandMinhasTarefas } from "@/store-zustand/admin/minhas-tarefas";
// hooks / zustand
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

// domain
import { handleGetMinhasTarefas } from "@/domain/use-cases/my-tasks";
import { formatFuroLabel } from "@/utils/format-furo-label";

export interface IModeloTecnicoMinhasTarefas {
  id: number;
  nome_modelo: string;
  is_provisorio?: boolean;
  tem_carga_foto?: boolean;
  tem_dados_variaveis?: boolean;
  tem_furo?: boolean | number;
  tipo_furo?: string | null;
}

export interface ITarefas {
  id: number;
  user_id: number;
  modelo_tecnico_id: number;
  tecnologia_id: number;
  total_solicitacoes: number;
  situacao: string;
  status: string;
  data_remessa: string;
  data_inicio_producao: string;
  data_fim_producao: string;
  posicao: string;
  observacao: string;
  prazo: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  cliente: any;
  tecnologia: any;
  modelo_tecnico: IModeloTecnicoMinhasTarefas | null;
  solicitante: any;
  designer: any;
  consultor?: any; // opcional, você usa no map
  responsavel?: string; // idem
  numero_remessa: string;
  is_provisorio?: boolean;
  tem_carga_foto?: boolean;
  tem_dados_variaveis?: boolean;
}

export interface IPagination {
  current_page: number;
  per_page: number;
  // API Nest: total da lista (já filtrado pela busca) e última página
  total?: number;
  last_page?: number;
  has_more_pages: boolean;
  next_page_url: string | null;
  previous_page_url: string | null;
}

export interface ITarefasResponse {
  code: number;
  status: string;
  message: string;
  data: ITarefas[];
  pagination: IPagination;
  total_remessas_solicitadas?: number;
  total_remessas_em_producao?: number;
}

function derivePaginationMeta(remessaResponse: ITarefasResponse | undefined, itemsPerPage: number) {
  const pagination = remessaResponse?.pagination;
  const perPage = pagination?.per_page ?? itemsPerPage;
  // A API Nest manda o total em pagination.total; o campo total_remessas_em_producao
  // só vinha do Laravel e fica de fallback.
  const total = pagination?.total ?? remessaResponse?.total_remessas_em_producao ?? 0;

  if (total > 0) {
    return {
      total,
      lastPage: pagination?.last_page ?? Math.max(1, Math.ceil(total / perPage)),
    };
  }

  const currentPage = pagination?.current_page ?? 1;
  const hasMorePages = pagination?.has_more_pages ?? false;

  return {
    total: 0,
    lastPage: hasMorePages ? currentPage + 1 : currentPage,
  };
}

function mapRemessasToTable(data: ITarefas[]): Record<string, any>[] {
  return data?.map((remessa) => ({
    remessa: remessa.id,
    numero_remessa: remessa.numero_remessa ?? "N/A",
    nome_identificacao: remessa.cliente?.nome ?? "N/A",
    quantidadeSolicitacao: remessa.total_solicitacoes ?? 0,
    situacao: remessa.situacao ?? "N/A",
    data: remessa.created_at ? new Date(remessa.created_at).toLocaleString("pt-BR") : "N/A",
    data_inicio_producao: remessa.data_inicio_producao
      ? new Date(remessa.data_inicio_producao).toLocaleString("pt-BR")
      : "N/A",
    data_fim_producao: remessa.data_fim_producao
      ? new Date(remessa.data_fim_producao).toLocaleString("pt-BR")
      : "N/A",
    prazo: `${remessa.prazo > 1 ? `${remessa.prazo} dias` : `${remessa.prazo} dia`}`,
    posicao: remessa.posicao ?? "N/A",
    tecnologia: remessa.tecnologia?.nome ?? "N/A",
    modelo: remessa.modelo_tecnico?.nome_modelo ?? "N/A",
    modelo_id: remessa.modelo_tecnico?.id,
    furo: formatFuroLabel(remessa.modelo_tecnico?.tem_furo, remessa.modelo_tecnico?.tipo_furo),
    solicitante: remessa.solicitante?.nome ?? "N/A",
    designer: remessa?.designer?.nome ?? "N/A",
    observacao: remessa.observacao ?? "",
    is_provisorio: remessa.is_provisorio ?? false,
    tem_carga_foto: remessa.tem_carga_foto ?? remessa.modelo_tecnico?.tem_carga_foto ?? false,
    tem_dados_variaveis:
      remessa.tem_dados_variaveis ?? remessa.modelo_tecnico?.tem_dados_variaveis ?? false,
    hasMensagem: Boolean(remessa.observacao !== null),
  }));
}

export function generatePagination(
  currentPage: number,
  lastPage: number,
  delta = 2
): (number | string)[] {
  const range: (number | string)[] = [];
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(lastPage - 1, currentPage + delta);

  range.push(1);

  if (left > 2) {
    range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < lastPage - 1) {
    range.push("...");
  }

  if (lastPage > 1) {
    range.push(lastPage);
  }

  return range;
}

function useMinhasTarefasAdmin() {
  const { currentPage, itemsPerPage, searchItem, setPaginations, setLastPage, setTotalItemsPage } =
    useStoreZustandMinhasTarefas();

  const { token: accessToken } = useStoreZustandUserAuth();

  const queryKey = ["minhas-tarefas-admin", currentPage, itemsPerPage, searchItem, searchItem];

  const {
    isLoading,
    error: isError,
    data: remessaResponse,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const minhasTarefasResponse = await handleGetMinhasTarefas(
        accessToken!,
        currentPage,
        itemsPerPage,
        searchItem!
      );

      return minhasTarefasResponse;
    },
  });

  const tarefas = mapRemessasToTable(remessaResponse?.data);

  const { total, lastPage } = derivePaginationMeta(remessaResponse, itemsPerPage);

  const pages = useMemo(() => {
    return generatePagination(currentPage, lastPage);
  }, [currentPage, lastPage]);

  useEffect(() => {
    setPaginations(pages as number[]);
    setLastPage(lastPage);
    setTotalItemsPage(total);
  }, [pages, setPaginations, setLastPage, lastPage, total, setTotalItemsPage]);

  return {
    minhasTarefas: tarefas ?? [],
    paginations: pages,
    total_remessas_em_producao: total,
    isLoading,
    isError,
  };
}

export { useMinhasTarefasAdmin };
